import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { DeleteModal } from "@View/components/DeleteModal";
import { connect } from "react-redux";
import { deleteUser } from "@State/";
import DataTable from "@View/components/table/DataTable";
import { usePageRoot } from "@View/hooks/usePageRoot";
import { formatDate } from "@Localization/utils";
import ButtonAddNew from "@View/components/buttons/ButtonAddNew";
import NewUserPage from "@View/pages/AdminPO/user/NewUserPage";
import { Tooltip } from "@material-ui/core";
import EditIcon from "@View/components/svgComponents/EditIcon";
import DeleteIcon from "@View/components/svgComponents/DeleteIcon";
import DetailsIcon from "@View/components/svgComponents/DetailsIcon";
import EditUserPage from "@View/pages/AdminPO/user/EditUserPage";
import UserDetailsPage from "@View/pages/AdminPO/user/UserDetailsPage";
import {
  canAddClientUser,
  canCreateRequest,
  canDeleteClient,
  canEditEverything,
  getRole,
} from "@Security/stateHelpers";
import { ShouldHideUserEdit } from "@Constants/tokens";
import { RangeDatePicker } from "@Components/form/control/RangeDatePicker";
import { useTooltipStyle } from "@View/style/components/tooltipStyles";

//redux
const mapStateToProps = (state) => {
  return {
    users: state.client?.client?.users ?? [],
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    deleteUser: (userId, clientId) => dispatch(deleteUser(userId, clientId)),
  };
};

const UsersTable = ({ users, deleteUser, clientId, dateFilter = null }) => {
  const { t } = useTranslation();
  const [deletedId, setDeletedId] = useState();
  const tooltipStyle = useTooltipStyle();

  const handleOpenModal = (event, usetId) => {
    event.preventDefault();
    setDeletedId(usetId);
  };

  const handleCloseModal = (event) => {
    event.preventDefault();
    setDeletedId();
  };

  const handleDelete = (event) => {
    event.preventDefault();
    deleteUser(deletedId, clientId);
    setDeletedId();
  };

  const [userId, setUserId] = React.useState();
  const [openDetails, setOpenDetails] = React.useState(false);

  const handleOpenDetails = () => {
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const [openNew, setOpenNew] = React.useState(false);

  const handleOpenNew = () => {
    setOpenNew(true);
  };

  const handleCloseNew = () => {
    setOpenNew(false);
  };

  const [openEdit, setOpenEdit] = React.useState(false);

  const handleOpenEdit = () => {
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const headTableUsers = [
    {
      field: "name",
      headerName: t("FullName"),
      flex: 1,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "createdAt",
      headerName: t("inputs.Create Date"),
      flex: 0.7,
      headerAlign: "left",
      align: "left",
      valueGetter: (cell) => formatDate(cell.value),
    },
    {
      field: "status",
      headerName: t("inputs.Status"),
      flex: 0.7,
      headerAlign: "left",
      align: "left",
      valueGetter: (cell) => t(cell.row.status),
    },
    {
      field: "actions",
      headerName: " ",
      flex: 1,
      sortable: false,
      filterable: false,
      headerAlign: "left",
      renderCell: (cell) => {
        return (
          <div className="tableIcons">
            <Tooltip title={t("buttons.Details")} classes={tooltipStyle}>
              <div
                onClick={() => {
                  setUserId(cell.row.id);
                  handleOpenDetails();
                }}
                className={tooltipStyle.element}
              >
                <DetailsIcon />
              </div>
            </Tooltip>

            {canEditEverything() && !ShouldHideUserEdit() && (
              <>
                <Tooltip title={t("buttons.Edit")} classes={tooltipStyle}>
                  <div
                    onClick={() => {
                      setUserId(cell.row.id);
                      handleOpenEdit();
                    }}
                    className={tooltipStyle.element}
                  >
                    <EditIcon className="detailsIcon" />
                  </div>
                </Tooltip>
                {canDeleteClient() && (
                  <Tooltip title={t("buttons.Delete")} classes={tooltipStyle}>
                    <div
                      onClick={(event) => handleOpenModal(event, cell.row.id)}
                      className={tooltipStyle.element}
                    >
                      <DeleteIcon />
                    </div>
                  </Tooltip>
                )}
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      {openNew ? (
        <NewUserPage
          prevPage={"client"}
          open={openNew}
          handleOpen={handleOpenNew}
          handleClose={handleCloseNew}
        />
      ) : null}
      <UserDetailsPage
        userId={userId}
        open={openDetails}
        handleOpen={handleOpenDetails}
        handleClose={handleCloseDetails}
      />

      {openEdit ? (
        <EditUserPage
          userId={userId}
          open={openEdit}
          handleOpen={handleOpenEdit}
          handleClose={handleCloseEdit}
        />
      ) : null}
      <DataTable
        rows={users}
        columns={headTableUsers}
        toolbarButtons={
          <>
            {dateFilter && (
              <RangeDatePicker
                views={["date"]}
                startValue={dateFilter.startDate}
                endValue={dateFilter.endDate}
                onChangeStart={dateFilter.setStartDate}
                onChangeEnd={dateFilter.setEndDate}
                flex
              />
            )}
            {canAddClientUser() && <ButtonAddNew onClick={handleOpenNew} />}
          </>
        }
      />
      <DeleteModal
        handleDelete={handleDelete}
        openModal={deletedId !== undefined}
        handleCloseModal={handleCloseModal}
      />
    </React.Fragment>
  );
};

UsersTable.propTypes = {
  users: PropTypes.array.isRequired,
  deleteUser: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(UsersTable);
